import React, { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import {
  getGames,
  getPatientGameSettingsForGame,
  updatePatientGameSettings,
} from "../../services/GameService";
import { DEFAULT_TIME_PROGRESSION } from "../TimeMatch/timeProgression";
import "./PatientGameSettings.css";

const MINUTE_OPTIONS = [0, 15, 30, 45];

// Friendly preset progressions (no raw JSON) the clinician can start from.
const PRESETS = {
  "Whole hours only": [
    { hours: 3, minutes: 0 },
    { hours: 4, minutes: 0 },
    { hours: 5, minutes: 0 },
  ],
  "Add half hours": [
    { hours: 3, minutes: 0 },
    { hours: 4, minutes: 0 },
    { hours: 3, minutes: 30 },
    { hours: 4, minutes: 30 },
  ],
  "Full progression": DEFAULT_TIME_PROGRESSION,
};

const isClockGame = (game) =>
  Boolean(game && /time-match/.test(game.game_route_path || ""));

// Stepper + dropdown editor for an ordered list of clock target times.
const ClockProgressionEditor = ({ steps, setSteps }) => {
  const setStep = (i, patch) =>
    setSteps(steps.map((s, idx) => (idx === i ? { ...s, ...patch } : s)));
  const removeStep = (i) => setSteps(steps.filter((_, idx) => idx !== i));
  const addStep = () => setSteps([...steps, { hours: 3, minutes: 0 }]);

  return (
    <div className="pgs-editor">
      <div className="pgs-presets">
        <span>Start from a preset:</span>
        {Object.keys(PRESETS).map((name) => (
          <button
            key={name}
            type="button"
            className="pgs-btn"
            onClick={() => setSteps(PRESETS[name])}
          >
            {name}
          </button>
        ))}
      </div>

      <ol className="pgs-steps">
        {steps.map((step, i) => (
          <li key={i} className="pgs-step">
            <span className="pgs-step-num">{i + 1}.</span>
            <button
              type="button"
              className="pgs-stepper"
              aria-label={`Decrease hour for step ${i + 1}`}
              onClick={() =>
                setStep(i, { hours: Math.max(1, step.hours - 1) })
              }
            >
              −
            </button>
            <span className="pgs-hours" aria-label={`Hour for step ${i + 1}`}>
              {step.hours}
            </span>
            <button
              type="button"
              className="pgs-stepper"
              aria-label={`Increase hour for step ${i + 1}`}
              onClick={() =>
                setStep(i, { hours: Math.min(12, step.hours + 1) })
              }
            >
              +
            </button>
            <span className="pgs-colon">:</span>
            <select
              className="pgs-minutes"
              aria-label={`Minutes for step ${i + 1}`}
              value={step.minutes}
              onChange={(e) =>
                setStep(i, { minutes: Number(e.target.value) })
              }
            >
              {MINUTE_OPTIONS.map((m) => (
                <option key={m} value={m}>
                  {String(m).padStart(2, "0")}
                </option>
              ))}
            </select>
            <button
              type="button"
              className="pgs-remove"
              aria-label={`Remove step ${i + 1}`}
              onClick={() => removeStep(i)}
              disabled={steps.length <= 1}
            >
              Remove
            </button>
          </li>
        ))}
      </ol>

      <button type="button" className="pgs-btn" onClick={addStep}>
        Add step
      </button>
    </div>
  );
};

// Admin Mode: configure a patient's per-game settings with friendly controls.
const PatientGameSettings = () => {
  const { id } = useParams();
  const [games, setGames] = useState([]);
  const [gameId, setGameId] = useState("");
  const [steps, setSteps] = useState(DEFAULT_TIME_PROGRESSION);
  const [loading, setLoading] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    getGames()
      .then(setGames)
      .catch(() => {});
  }, []);

  const selectedGame = games.find(
    (g) => String(g.game_id) === String(gameId)
  );

  const loadSettings = useCallback(
    async (gid) => {
      if (!gid) return;
      setLoading(true);
      setSaved(false);
      setError("");
      try {
        const data = await getPatientGameSettingsForGame(id, gid);
        const prog =
          data.settings && Array.isArray(data.settings.time_progression)
            ? data.settings.time_progression
            : DEFAULT_TIME_PROGRESSION;
        setSteps(prog);
      } catch (e) {
        setError("Could not load settings.");
      } finally {
        setLoading(false);
      }
    },
    [id]
  );

  const handleGameChange = (e) => {
    setGameId(e.target.value);
    loadSettings(e.target.value);
  };

  const handleSave = async () => {
    setError("");
    setSaved(false);
    try {
      await updatePatientGameSettings(id, gameId, {
        time_progression: steps,
      });
      setSaved(true);
    } catch (e) {
      setError("Could not save settings.");
    }
  };

  return (
    <div className="patient-game-settings">
      <Link to={`/admin/patients/${id}`} className="pgs-back">
        ← Back to patient
      </Link>
      <h1>Configure games</h1>

      <label className="pgs-game-select">
        Game
        <select value={gameId} onChange={handleGameChange}>
          <option value="">Choose a game…</option>
          {games.map((g) => (
            <option key={g.game_id} value={g.game_id}>
              {g.game_name}
            </option>
          ))}
        </select>
      </label>

      {error && (
        <p className="pgs-error" role="alert">
          {error}
        </p>
      )}

      {gameId && !loading && (
        <>
          {isClockGame(selectedGame) ? (
            <>
              <p className="pgs-help">
                The patient practises these target times in order, advancing as
                they succeed.
              </p>
              <ClockProgressionEditor steps={steps} setSteps={setSteps} />
              <button
                type="button"
                className="pgs-btn pgs-btn-primary"
                onClick={handleSave}
              >
                Save settings
              </button>
              {saved && (
                <p className="pgs-saved" role="status">
                  Saved. This takes effect next time the patient plays.
                </p>
              )}
            </>
          ) : (
            <p className="pgs-empty">
              No configurable settings for this game yet.
            </p>
          )}
        </>
      )}
    </div>
  );
};

export default PatientGameSettings;
